<script setup>
// All of the contact's tickets are listed, but appending is allowed ONLY to a ticket that
// belongs to THIS conversation (its own ticket). Older tickets from other conversations are
// shown but LOCKED (disabled). If the conversation has no ticket yet, the agent must create
// one first.
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { emitter } from 'shared/helpers/mitt';
import { CMD_CONVERT_TO_TICKET } from 'dashboard/helper/commandbar/events';
import TicketsAPI from 'dashboard/api/tickets';
import MessageAPI from 'dashboard/api/inbox/message';
import wootConstants from 'dashboard/constants/globals';

const props = defineProps({
  conversation: { type: Object, required: true },
  onClose: { type: Function, required: true },
});

const store = useStore();

const tickets = ref([]);
const selectedTicketId = ref(null);
const isLoading = ref(true);
const isSaving = ref(false);

const contactId = computed(() => props.conversation.meta?.sender?.id);

// A ticket may be appended to only when it was created for THIS conversation.
const appendable = ticket =>
  String(ticket.conversation_display_id) === String(props.conversation.id);

const ownTickets = computed(() => tickets.value.filter(appendable));

const selectedTicket = computed(
  () => tickets.value.find(tk => tk.id === selectedTicketId.value) ?? null
);

onMounted(async () => {
  if (!contactId.value) {
    isLoading.value = false;
    return;
  }
  try {
    const { data } = await TicketsAPI.appendable(contactId.value);
    tickets.value = Array.isArray(data) ? data : [];
    if (ownTickets.value.length === 1) selectedTicketId.value = ownTickets.value[0].id;
  } finally {
    isLoading.value = false;
  }
});

const formatDate = ts => {
  if (!ts) return '';
  return new Date(ts * 1000).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

const createNewTicket = () => {
  props.onClose();
  emitter.emit(CMD_CONVERT_TO_TICKET);
};

const submit = async () => {
  if (!selectedTicket.value || !appendable(selectedTicket.value)) return;
  isSaving.value = true;
  try {
    await MessageAPI.create({
      conversationId: props.conversation.id,
      message: `This conversation was resolved and linked to ticket **#${selectedTicket.value.id}: ${selectedTicket.value.subject}**`,
      private: true,
    });
    await store.dispatch('toggleStatus', {
      conversationId: props.conversation.id,
      status: wootConstants.STATUS_TYPE.RESOLVED,
    });
    useAlert(`Conversation resolved and linked to ticket #${selectedTicket.value.id}`);
    props.onClose();
  } catch {
    useAlert('Failed to resolve and append. Please try again.');
  } finally {
    isSaving.value = false;
  }
};
</script>

<template>
  <div
    class="fixed inset-0 z-[9999] flex items-center justify-end"
    @click.self="onClose"
  >
    <div class="w-[480px] h-full bg-n-solid-1 border-l border-n-weak flex flex-col shadow-2xl">
      <!-- Header -->
      <div class="flex items-start justify-between px-6 py-5 border-b border-n-weak">
        <div>
          <h2 class="text-base font-semibold text-n-slate-12">
            Resolve and append to this conversation's ticket
          </h2>
          <p class="text-sm text-n-slate-9 mt-0.5">
            You can only append to a ticket created for this conversation. Older tickets are locked.
          </p>
        </div>
        <button class="text-n-slate-9 hover:text-n-slate-12 mt-0.5" @click="onClose">
          <span class="i-lucide-x size-5" />
        </button>
      </div>

      <!-- Body -->
      <div class="flex-1 overflow-y-auto px-6 py-4">
        <div v-if="isLoading" class="flex items-center justify-center py-12">
          <span class="i-lucide-loader-2 size-6 text-n-slate-9 animate-spin" />
        </div>

        <div v-else-if="tickets.length === 0" class="text-center py-12 text-sm text-n-slate-9">
          No tickets found for this contact
        </div>

        <div v-else class="flex flex-col gap-2">
          <div
            v-if="ownTickets.length === 0"
            class="flex items-start gap-2 text-xs text-n-slate-11 bg-n-alpha-1 border border-n-weak rounded-md px-3 py-2 mb-1"
          >
            <span class="i-lucide-shield-alert size-4 text-n-ruby-9 shrink-0 mt-0.5" />
            <span>
              This conversation has no ticket yet — the tickets below belong to other
              conversations and are locked. <strong>Create a new ticket</strong> to append.
            </span>
          </div>

          <label
            v-for="ticket in tickets"
            :key="ticket.id"
            class="flex items-start gap-3 p-3 rounded-lg border transition-colors"
            :class="[
              appendable(ticket) ? 'cursor-pointer' : 'opacity-60 cursor-not-allowed',
              selectedTicketId === ticket.id
                ? 'border-n-brand bg-n-alpha-1'
                : appendable(ticket) ? 'border-n-weak hover:bg-n-alpha-1' : 'border-n-weak',
            ]"
          >
            <input
              v-model="selectedTicketId"
              type="radio"
              :value="ticket.id"
              :disabled="!appendable(ticket)"
              class="mt-0.5 accent-n-brand shrink-0"
            />
            <div class="flex-1 min-w-0">
              <p
                class="text-sm font-medium truncate"
                :class="appendable(ticket) ? 'text-n-brand' : 'text-n-slate-11'"
              >
                {{ ticket.subject }} #{{ ticket.id }}
              </p>
              <p class="text-xs text-n-slate-9 mt-0.5">
                Ticket · {{ ticket.contact?.name || 'Unknown' }} · Created {{ formatDate(ticket.created_at) }}
              </p>
              <p v-if="!appendable(ticket)" class="text-xs text-n-ruby-11 mt-0.5 flex items-center gap-1">
                <span class="i-lucide-lock size-3" /> Belongs to another conversation — can't append
              </p>
            </div>
            <span
              class="shrink-0 text-xs px-2 py-0.5 rounded-full font-medium mt-0.5"
              :class="{
                'bg-green-100 text-green-800': ticket.status === 'open',
                'bg-yellow-100 text-yellow-800': ticket.status === 'pending',
                'bg-blue-100 text-blue-800': ticket.status === 'resolved',
                'bg-slate-100 text-slate-600': ticket.status === 'closed',
              }"
            >
              {{ ticket.custom_status || ticket.status }}
            </span>
          </label>
        </div>
      </div>

      <!-- Footer -->
      <div class="flex items-center justify-end gap-3 px-6 py-4 border-t border-n-weak">
        <button
          class="px-4 py-2 text-sm font-medium text-n-slate-11 hover:text-n-slate-12 transition-colors"
          @click="onClose"
        >
          Cancel
        </button>
        <button
          class="px-4 py-2 text-sm font-medium rounded-lg border border-n-weak text-n-slate-12 hover:bg-n-alpha-1 transition-colors"
          @click="createNewTicket"
        >
          Create New Ticket
        </button>
        <button
          class="px-4 py-2 text-sm font-medium rounded-lg transition-colors"
          :class="selectedTicket && !isSaving
            ? 'bg-n-brand text-white hover:bg-n-brand/90 cursor-pointer'
            : 'bg-n-alpha-2 text-n-slate-9 cursor-not-allowed'"
          :disabled="!selectedTicket || isSaving"
          @click="submit"
        >
          {{ isSaving ? 'Saving...' : 'Resolve And Append' }}
        </button>
      </div>
    </div>
  </div>
</template>
